import React, { useState, useRef, useEffect } from 'react';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { useTheme, Grid, Paper, Box, Typography, Container, Fab, Tooltip, CircularProgress, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, TextField, Button } from '@mui/material';
import { Refresh as RefreshIcon, ExpandMore as ExpandMoreIcon, 
  ChevronRight as ChevronRightIcon, CameraAlt as CameraIcon, FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { useAuth } from '../../context/auth-provider';

const MODEL_NAME = 'hsq-find-matches-transform';
const MODEL_VERSION = '1.3';
const LABEL_COUNT = '300';
const PRECISION_RECALL = '.9';
const ACCURACY_COST = '.9';
const AUC_PR = '0.997';
const PRECISION = '1';
const RECALL_UPPER_LIMIT = '0.454';
const F1 = '0.625';

interface IAxiosInstance extends AxiosInstance {
  [key: string]: any;
}

export default function HsqView() {
  
  const theme = useTheme();
  const { user, } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<IHsqAddress | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);  
  const axiosInstance = useRef<IAxiosInstance | null>(null);
  const abortController = useRef<AbortController>(new AbortController());

  const initAxiosInstance = () => {
    if (!user || axiosInstance.current) return;
    axiosInstance.current = axios.create({
      baseURL: process.env.REACT_APP_HSQ_API_URL!,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Authorization': user.idToken
      }
    });
  };

  const loadHsqAddress = async (id: string) : Promise<void> => {

    if (!user) return;

    // Ensure axios instance initialized with auth header
    initAxiosInstance();

    try {

      // Show loading spinner
      setIsLoading(true);   

      console.log(`event :: searching: ${`/addresses/${id}`}`);
      
      // Api call
      const response = await axiosInstance.current!.get(
        `/addresses/${id}`, 
        { signal: abortController.current.signal });

      const item = response.data['Item'] as IHsqAddress;
      setSelectedItem(item);

      // console.log(`HsqView.getHsqAddress :: search: ${id}, response: ${JSON.stringify(response)}`);

    } catch (err: any) {

      // let msg = `There has been an error adding a snapshot for: ${clientId}/${snapshotDate}`;
      // if (err.response && err.response.data) {
      //   msg = err.response.data['Message'];
      // };

      setSelectedItem(null);
      console.log(`HsqView.getHsqAddress :: error :: error: ${err.message}`);
      // setSnapshotsError(msg);


    } finally {

      setIsLoading(false);

    }
  };

  const handleSearchButtonClick = async () => {
    if (!search) return;
    await loadHsqAddress(search)
  };

  const tableHeaderStyle = {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem', 
    fontWeight: '900',
    height: '40px',
    paddingTop: 0,
    paddingBottom: 0
  };

  const tableCellStyle = {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    height: '40px',
    paddingTop: 0,
    paddingBottom: 0,
  };

  const detailColumnStyle = {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    paddingTop: 0,
    paddingBottom: 0,
  };
  
  return (
    <>
      <Grid item xs={12} sx={{ position: 'relative', height: '100%' }}>

        {/* Loading overlay */}
        {isLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: 'rgba(255, 255, 255, 0.6)',
              zIndex: 999,
            }}>
            <CircularProgress />           
          </div>
        )}

        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', }}>

          <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: 'auto', backgroundColor: 'transparent' }}>
              
              {/* Title */}
              <Typography component="h2" variant="h6" color="primary" gutterBottom>
                High School Quality Search
              </Typography>
            
              {/* Model details */}
              <Paper
                sx={{ my: 2,  border: `solid 1px ${theme.palette.grey[100]}` }}>
                <Container 
                  component='div'
                  sx={{ p: 2, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                  <Stack component='div' sx={{ flex: '20%' }} direction="column">
                    <Typography component="h6" variant="body2" sx={detailColumnStyle} gutterBottom>
                      <strong>Model</strong>
                    </Typography>
                    <Typography component="h6" variant="body2" sx={detailColumnStyle} gutterBottom>
                      {MODEL_NAME}
                    </Typography>
                    <Typography component="h6" variant="body2" sx={detailColumnStyle} gutterBottom>
                      Version: {MODEL_VERSION}
                    </Typography>
                  </Stack>
                  <Stack component='div' sx={{ flex: '20%' }} direction="column">
                    <Typography component="h6" variant="body2" sx={detailColumnStyle} gutterBottom>
                      <strong>Training</strong>
                    </Typography>
                    <Typography component="h6" variant="body2" sx={detailColumnStyle} gutterBottom>
                      label count: {LABEL_COUNT}
                    </Typography>
                  </Stack>
                  <Stack component='div' sx={{ flex: '20%' }} direction="column">
                    <Typography component="h6" variant="body2" sx={detailColumnStyle} gutterBottom>
                      <strong>Tuning</strong>
                    </Typography>
                    <Typography component="h6" variant="body2" sx={detailColumnStyle} gutterBottom>
                      precision recall: {PRECISION_RECALL}
                    </Typography>
                    <Typography component="h6" variant="body2" sx={detailColumnStyle} gutterBottom>
                      accuracy cost: {ACCURACY_COST}
                    </Typography>
                  </Stack>
                  <Stack component='div' sx={{ flex: '20%' }} direction="column">
                    <Typography component="h6" variant="body2" sx={detailColumnStyle} gutterBottom>
                      <strong>Quality</strong>
                    </Typography>
                    <Typography component="h6" variant="body2" sx={detailColumnStyle} gutterBottom>
                      AUC-PR: {AUC_PR}
                    </Typography>
                    <Typography component="h6" variant="body2" sx={detailColumnStyle} gutterBottom>
                      Precision: {PRECISION}
                    </Typography>
                  </Stack>
                  <Stack component='div' sx={{ flex: '20%' }} direction="column">
                    <Typography component="h6" variant="body2" sx={detailColumnStyle} gutterBottom>
                      <br/>
                    </Typography>
                    <Typography component="h6" variant="body2" sx={detailColumnStyle} gutterBottom>
                      Recall upper limit: {RECALL_UPPER_LIMIT}
                    </Typography>
                    <Typography component="h6" variant="body2" sx={detailColumnStyle} gutterBottom>
                      F1: {F1}
                    </Typography>
                  </Stack>                 
                </Container>
              </Paper>
              
              {/* Search */}
              <Box 
                component={'div'} 
                sx={{ 
                  display: 'flex', flexDirection: 'row', 
                  justifyContent: 'flex-start', alignItems: 'center', 
                  backgroundColor: 'transparent',
                  pt: 1,
                  pb: 2,
                }}>

                <TextField 
                  id="ceeb-txt" 
                  label='CEEB'
                  size="small"
                  variant="outlined"
                  type='text'
                  disabled={false}
                  error={false}
                  sx={{ mr: 2 }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                
                <Button 
                  type='submit' 
                  variant='contained' 
                  color='primary'
                  onClick={handleSearchButtonClick}>
                  Search
                </Button>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, backgroundColor: 'transparent'  }}>
              
              {/* Results table */}
              { selectedItem && 
                <TableContainer 
                  component={Paper}
                  sx={{ mt:1 }}>
                  <Table sx={{ width: '100%' }} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell align="left" style={{ width: '15%', ...tableHeaderStyle }}>Attr</TableCell>
                        <TableCell align="left" style={{ width: '28%', ...tableHeaderStyle }}>Backbone</TableCell>
                        <TableCell align="left" style={{ width: '28%', ...tableHeaderStyle }}>NCES</TableCell>   
                        <TableCell align="left" style={{ width: '29%', ...tableHeaderStyle }}>PSS</TableCell>              
                      </TableRow>
                    </TableHead>
                    <TableBody>                 
                      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>                  
                        <TableCell align="left" style={{...tableCellStyle}}>Id</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.id_bb}</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.id_nces}</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.id_pss}</TableCell>
                      </TableRow>
                      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>                  
                        <TableCell align="left" style={{...tableCellStyle}}>School</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.school_bb}</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.school_nces}</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.school_pss}</TableCell>
                      </TableRow>
                      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>                  
                        <TableCell align="left" style={{...tableCellStyle}}>Address</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.address_bb}</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.address_nces}</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.address_pss}</TableCell>
                      </TableRow>
                      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>                  
                        <TableCell align="left" style={{...tableCellStyle}}>City</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.city_bb}</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.city_nces}</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.city_pss}</TableCell>
                      </TableRow>
                      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>                  
                        <TableCell align="left" style={{...tableCellStyle}}>State</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.state_bb}</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.state_nces}</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.state_pss}</TableCell>
                      </TableRow>
                      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>                  
                        <TableCell align="left" style={{...tableCellStyle}}>Zip5</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.zip5_bb}</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.zip5_nces}</TableCell>
                        <TableCell align="left" style={{...tableCellStyle}}>{selectedItem.zip5_pss}</TableCell>
                      </TableRow>
      
                    </TableBody>
                  </Table>
                </TableContainer>
              }
              
            </Box>
          </Box>
        </Paper>
      </Grid>
    </>
  )
}


          {/* <Paper component='div' sx={{ width: '100%', height: '100%', m: 0, mt: 2, p: 0 }}> */}